import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useFinancial } from '../../../context/FinancialContext';
import RoadmapDiagramModal from '../RoadmapDiagramModal';
import { WALKTHROUGH_STEPS } from './walkthroughConfig';

const { FiX, FiChevronRight, FiChevronLeft, FiCheck, FiMap } = FiIcons;
const ICON_STYLES = {
  amber: 'bg-amber-50 text-amber-600',
  slate: 'bg-slate-100 text-slate-600',
  indigo: 'bg-indigo-50 text-indigo-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  rose: 'bg-rose-50 text-rose-600',
  purple: 'bg-purple-50 text-purple-600',
  orange: 'bg-orange-50 text-orange-600',
  blue: 'bg-blue-50 text-blue-600',
  teal: 'bg-teal-50 text-teal-600'
};
const INITIAL_SHARED = {
  emergencyExpenses: null,
  retireSpending: null,
  multiplier: 25,
  hasMatch: null,
  debtTab: 'general'
};

function StepRail({ current, onSelect, progress, gatherComplete }) {
  return (
    <div className="roadmap-step-rail flex flex-wrap justify-center md:justify-start gap-2 mb-6" aria-label="Walkthrough steps">
      {WALKTHROUGH_STEPS.map((step, index) => {
        const done = index === 0 ? gatherComplete : progress.includes(step.roadmapId);
        const active = index === current;
        return (
          <button
            key={step.id}
            onClick={() => onSelect(index)}
            title={`Step ${index + 1}: ${step.title}`}
            aria-label={`Step ${index + 1}: ${step.title}${done ? ', complete' : ''}`}
            aria-current={active ? 'step' : undefined}
            className={`w-9 h-9 rounded-xl text-xs font-black flex items-center justify-center transition-all ${
              active ? 'bg-slate-900 text-white shadow-lg shadow-slate-300' : done ? 'bg-emerald-100 text-emerald-600 hover:bg-emerald-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
            }`}
          >
            {done && !active ? <SafeIcon icon={FiCheck} /> : index + 1}
          </button>
        );
      })}
    </div>
  );
}

function GuideContent({ step, current, shared, patch, onOpenDiagram }) {
  const { Tool, Visual } = step;
  const displayLabel = current === 0 ? 'Step 1 • Three-month picture' : step.label.replace(/Step \d+/, `Step ${current + 1}`);
  return (
    <motion.div key={current} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-5">
      <div className="flex items-center gap-3">
        {current === 1 ? (
          <button onClick={onOpenDiagram} title="View Interactive Diagram" aria-label="Open interactive financial roadmap diagram" className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 bg-blue-50 text-blue-600 hover:bg-blue-100 transition-all shadow-inner">
            <SafeIcon icon={FiMap} className="text-xl" />
          </button>
        ) : (
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shadow-inner flex-shrink-0 ${ICON_STYLES[step.color] || ICON_STYLES.slate}`}>
            <SafeIcon icon={step.icon} className="text-xl" />
          </div>
        )}
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{displayLabel}</p>
          {current === 1 && <button onClick={onOpenDiagram} className="text-xs font-bold text-blue-600 hover:underline">Open roadmap diagram</button>}
        </div>
      </div>
      <div>
        <h4 className="text-2xl md:text-3xl font-black text-slate-900 mb-3 tracking-tight leading-tight">{step.title}</h4>
        <p className="text-slate-600 text-sm leading-relaxed font-medium">{step.description}</p>
      </div>
      <Tool shared={shared} patch={patch} />
      <div className="roadmap-step-visual md:hidden pt-5 border-t border-slate-100">
        <Visual shared={shared} />
      </div>
    </motion.div>
  );
}

const RoadmapWalkthroughModal = ({ isOpen, onClose }) => {
  const { state, dispatch } = useFinancial();
  const [current, setCurrent] = useState(0);
  const [gatherComplete, setGatherComplete] = useState(false);
  const [diagramOpen, setDiagramOpen] = useState(false);
  const [shared, setShared] = useState(INITIAL_SHARED);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 });
  }, [current]);

  useEffect(() => {
    if (diagramOpen) return;
    const onKey = event => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, diagramOpen]);

  if (!isOpen) return null;
  const step = WALKTHROUGH_STEPS[current];
  const total = WALKTHROUGH_STEPS.length;
  const doneCount = WALKTHROUGH_STEPS.slice(1).filter(item => state.roadmapProgress.includes(item.roadmapId)).length + Number(gatherComplete);
  const patch = changes => setShared(previous => ({ ...previous, ...changes }));
  const completeCurrent = () => {
    if (current === 0) setGatherComplete(true);
    else if (!state.roadmapProgress.includes(step.roadmapId)) {
      dispatch({ type: 'TOGGLE_ROADMAP_STEP', payload: step.roadmapId });
    }
  };
  const goNext = () => {
    completeCurrent();
    if (current === total - 1) onClose();
    else setCurrent(index => index + 1);
  };

  return (
    <>
      <div className="roadmap-walkthrough fixed inset-0 z-[300] flex items-center justify-center p-2 sm:p-3 md:p-4 bg-slate-900/60 backdrop-blur-md">
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white w-full max-w-6xl h-[calc(100dvh-1rem)] sm:h-[92vh] md:h-[85vh] rounded-[1.5rem] md:rounded-[2.5rem] shadow-2xl relative overflow-hidden flex flex-col md:flex-row">
          <div className="absolute top-0 left-0 w-full h-1.5 bg-slate-100 z-50">
            <motion.div className="h-full bg-gradient-to-r from-blue-600 to-indigo-600" animate={{ width: `${((current + 1) / total) * 100}%` }} />
          </div>
          <div className="w-full md:w-1/2 flex-1 md:flex-none flex flex-col min-h-0 relative z-10">
            <div ref={scrollRef} className="flex-1 overflow-y-auto custom-scrollbar px-5 sm:px-6 md:px-10 pt-8 pb-6">
              <div className="flex justify-between items-start gap-3 mb-5">
                <div>
                  <h3 className="text-[10px] font-black uppercase text-blue-600 tracking-[0.2em] mb-1">Financial Roadmap</h3>
                  <h2 className="text-lg font-black text-slate-900 tracking-tight">
                    Step {current + 1} of {total}
                    <span className="inline-block ml-2 text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md align-middle">{doneCount}/{total} complete</span>
                  </h2>
                </div>
                <button onClick={onClose} aria-label="Close walkthrough" className="md:hidden p-2 text-slate-400 hover:text-slate-600 flex-shrink-0">
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>
              <StepRail current={current} onSelect={setCurrent} progress={state.roadmapProgress} gatherComplete={gatherComplete} />
              <GuideContent step={step} current={current} shared={shared} patch={patch} onOpenDiagram={() => setDiagramOpen(true)} />
            </div>
            <div className="p-3 sm:p-4 md:px-10 md:py-5 flex gap-2 sm:gap-3 border-t border-slate-100 bg-white">
              <button aria-label="Previous step" disabled={current === 0} onClick={() => setCurrent(index => index - 1)} className={`w-12 flex-shrink-0 py-3 rounded-xl font-black flex items-center justify-center ${current === 0 ? 'bg-slate-50 text-slate-300' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                <SafeIcon icon={FiChevronLeft} />
              </button>
              <button onClick={goNext} className="flex-1 min-w-0 px-2 py-3 bg-slate-900 text-white rounded-xl font-black text-[10px] sm:text-xs uppercase tracking-wide flex items-center justify-center gap-2 hover:bg-black transition-all shadow-lg shadow-slate-200">
                {current === total - 1 ? 'Finish Roadmap' : 'Complete & Continue'}
                <SafeIcon icon={FiChevronRight} className="flex-shrink-0" />
              </button>
            </div>
          </div>
          <div className="hidden md:flex md:w-1/2 bg-slate-50 border-l border-slate-100 relative min-h-0" style={{ backgroundImage: 'radial-gradient(#e2e8f0 1px,transparent 1px)', backgroundSize: '18px 18px' }}>
            <button onClick={onClose} aria-label="Close walkthrough" className="absolute top-6 right-6 p-3 bg-white border border-slate-200 rounded-full text-slate-400 hover:text-slate-600 shadow-sm z-50">
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-8 pt-20">
              <motion.div key={current} initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}>
                <step.Visual shared={shared} />
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
      <RoadmapDiagramModal isOpen={diagramOpen} onClose={() => setDiagramOpen(false)} />
    </>
  );
};

export default RoadmapWalkthroughModal;